import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getAuth, 
  multiFactor, 
  PhoneAuthProvider, 
  RecaptchaVerifier,
  EmailAuthProvider,
  reauthenticateWithCredential,
  onAuthStateChanged,
  PhoneMultiFactorGenerator,
  getMultiFactorResolver
} from 'firebase/auth';
import Swal from 'sweetalert2';
import AdminLayout from './AdminLayout';

const TwoFactorAuthPage = () => {
  const [user, setUser] = useState(null);
  const [factors, setFactors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [removeStep, setRemoveStep] = useState('confirm');
  const [selectedFactorUid, setSelectedFactorUid] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationId, setVerificationId] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [removePassword, setRemovePassword] = useState('');
  const [removeCode, setRemoveCode] = useState('');
  const [removePhoneHint, setRemovePhoneHint] = useState('');
  const [step, setStep] = useState('phone');
  const [processing, setProcessing] = useState(false);
  
  const navigate = useNavigate();
  const auth = getAuth();
  const verifierRef = useRef(null);
  const removeResolverRef = useRef(null);
  const removeVerificationIdRef = useRef('');

  useEffect(() => {
    auth.settings.appVerificationDisabledForTesting = true;
    if (!verifierRef.current) {
      try {
        verifierRef.current = new RecaptchaVerifier(auth, 'mfa-enroll-recaptcha', {
          size: 'invisible',
        });
      } catch (e) {
        console.warn('RecaptchaVerifier init failed:', e.message);
      }
    }

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await loadFactors(currentUser);
      } else {
        navigate('/admin/login');
      }
    });
    return () => {
      unsubscribe();
      if (verifierRef.current) {
        try { verifierRef.current.clear(); } catch (_) {}
        verifierRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadFactors = async (currentUser) => {
    try {
      setLoading(true);
      await currentUser.reload();
      await currentUser.getIdToken(true);
      const freshUser = auth.currentUser;
      
      if (!freshUser) {
        setFactors([]);
        return;
      }
      
      const mfaUser = multiFactor(freshUser);
      const enrolledFactors = mfaUser.enrolledFactors || [];
      console.log('Enrolled factors:', enrolledFactors);
      setFactors(enrolledFactors);
    } catch (e) {
      console.error('Error loading factors:', e);
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneInput = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const getE164Phone = (formatted) => {
    const digits = formatted.replace(/\D/g, '');
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    return `+${digits}`;
  };

  const handleStartEnroll = async () => {
    const digits = phoneNumber.replace(/\D/g, '');
    if (digits.length !== 10) {
      Swal.fire('Error', 'Please enter a valid 10-digit US phone number', 'error');
      return;
    }
    setStep('password');
  };

  const handleSendCode = async () => {
    if (!password) {
      Swal.fire('Error', 'Please enter your password', 'error');
      return;
    }
    
    setProcessing(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("No user found.");

      const credential = EmailAuthProvider.credential(currentUser.email, password);
      await reauthenticateWithCredential(currentUser, credential);

      const mfaSession = await multiFactor(currentUser).getSession();

      if (!verifierRef.current) {
        verifierRef.current = new RecaptchaVerifier(auth, 'mfa-recaptcha', {
          size: 'invisible',
        });
      }

      const provider = new PhoneAuthProvider(auth);
      const vid = await provider.verifyPhoneNumber(
        {
          phoneNumber: getE164Phone(phoneNumber),
          session: mfaSession,
        },
        verifierRef.current
      );

      setVerificationId(vid);
      setStep('verify');
    } catch (e) {
      console.error('Send code error:', e);
      const errorMsg = e.code === 'auth/wrong-password' ? 'Incorrect password' : e.message;
      Swal.fire('Error', errorMsg || 'Failed to send code', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleVerifyAndEnroll = async () => {
    if (!code) {
      Swal.fire('Error', 'Please enter the verification code', 'error');
      return;
    }

    setProcessing(true);
    try {
      const currentUser = auth.currentUser;
      const phoneAuthCredential = PhoneAuthProvider.credential(verificationId, code);
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(phoneAuthCredential);
      await multiFactor(currentUser).enroll(multiFactorAssertion, 'Primary Mobile');

      Swal.fire('Success', 'Two-factor authentication enabled!', 'success');
      setShowAddModal(false);
      setStep('phone');
      await loadFactors(currentUser);
    } catch (e) {
      console.error('Enrollment error:', e);
      Swal.fire('Error', e.message || 'Failed to verify code', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleUnenroll = (factor) => {
    const uid = factor?.uid || (factors.length > 0 ? factors[0].uid : null);
    console.log('Preparing to remove factor with UID:', uid);
    setSelectedFactorUid(uid);
    setRemoveStep('confirm');
    setRemovePassword('');
    setRemoveCode('');
    removeResolverRef.current = null;
    removeVerificationIdRef.current = '';
    setShowRemoveModal(true);
  };

  const handleRemoveReauth = async () => {
    if (!removePassword) {
      Swal.fire('Error', 'Please enter your password', 'error');
      return;
    }
    if (!user || !user.email) {
      Swal.fire('Error', 'User session not available', 'error');
      return;
    }

    setProcessing(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, removePassword);
      await reauthenticateWithCredential(user, credential);
      await doUnenroll();
    } catch (e) {
      console.error('Reauth error:', e);
      if (e.code === 'auth/multi-factor-auth-required') {
        try {
          const resolver = getMultiFactorResolver(auth, e);
          removeResolverRef.current = resolver;
          
          const phoneHint = resolver.hints.find(h => h.factorId === 'phone');
          if (!phoneHint) throw new Error('No phone factor found for MFA challenge');

          setRemovePhoneHint(phoneHint.phoneNumber || 'Phone');

          if (!verifierRef.current) {
            verifierRef.current = new RecaptchaVerifier(auth, 'mfa-enroll-recaptcha', {
              size: 'invisible',
            });
          }

          const provider = new PhoneAuthProvider(auth);
          const vid = await provider.verifyPhoneNumber({
            multiFactorHint: phoneHint,
            session: resolver.session,
          }, verifierRef.current);
          
          removeVerificationIdRef.current = vid;
          setRemoveStep('mfa-code');
        } catch (mfaErr) {
          console.error('MFA setup error:', mfaErr);
          Swal.fire('Error', mfaErr.message || 'Failed to send MFA code', 'error');
        }
      } else if (e.code === 'auth/wrong-password') {
        Swal.fire('Error', 'Incorrect password', 'error');
      } else {
        Swal.fire('Error', e.message || 'Authentication failed', 'error');
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleRemoveMfaVerify = async () => {
    if (removeCode.length !== 6) {
      Swal.fire('Error', 'Enter 6-digit code', 'error');
      return;
    }

    setProcessing(true);
    try {
      const resolver = removeResolverRef.current;
      if (!resolver) {
        Swal.fire('Error', 'MFA session expired. Please try again.', 'error');
        setProcessing(false);
        return;
      }

      const credential = PhoneAuthProvider.credential(removeVerificationIdRef.current, removeCode);
      const assertion = PhoneMultiFactorGenerator.assertion(credential);
      await resolver.resolveSignIn(assertion);
      await doUnenroll();
    } catch (e) {
      console.error('MFA verify error:', e);
      Swal.fire('Error', e.message || 'Invalid code', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const doUnenroll = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        Swal.fire('Error', 'User session lost', 'error');
        return;
      }

      const mfaUser = multiFactor(currentUser); 
      const factorUid = selectedFactorUid || (factors.length > 0 ? factors[0].uid : null);
      
      console.log('Target Factor UID:', factorUid);

      if (factorUid) {
        try {
          console.log('Unenrolling factor UID:', factorUid);
          await mfaUser.unenroll(factorUid);
          console.log('Successfully unenrolled from Auth');
        } catch (unenrollErr) {
          console.error('Auth unenroll error:', unenrollErr);
          throw unenrollErr;
        }
      }

      setShowRemoveModal(false);
      setSelectedFactorUid(null);
      removeResolverRef.current = null;
      removeVerificationIdRef.current = '';
      
      await loadFactors(currentUser);
      Swal.fire('Removed', 'Two-factor authentication removed successfully', 'success');
    } catch (e) {
      console.error('Unenroll error:', e);
      Swal.fire('Error', e.message || 'Failed to remove', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const openAddModal = () => {
    setPhoneNumber('');
    setPassword('');
    setCode('');
    setStep('phone');
    setShowAddModal(true);
  };

  const handleRefresh = () => {
    const currentUser = auth.currentUser;
    if (currentUser) {
      loadFactors(currentUser);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Two-Factor Authentication">
        <div className="text-center py-5">
          <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 80, height: 80, objectFit: 'contain' }} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Two-Factor Authentication">
      <div className="two-factor-page">
        <div id="mfa-enroll-recaptcha"></div>

        <div className="row mb-4">
          <div className="col-12">
            <h4 className="font-weight-bold">Two-Factor Authentication</h4>
            <p className="text-muted">Add an extra layer of security to your account</p>
          </div>
        </div>

        <div className="row mb-2">
          <div className="col-md-8">
            <button className="btn btn-sm btn-outline-secondary mb-2" onClick={handleRefresh}>
              <i className="fas fa-sync-alt mr-1"></i>Refresh Status
            </button>
          </div>
        </div>

        {/* Status Card */}
        <div className="row mb-4">
          <div className="col-md-8">
            <div className="card" style={{ borderRadius: '16px', border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
              <div className="card-body p-4">
                <div className="d-flex align-items-center mb-3">
                  <div className={`d-flex align-items-center justify-content-center mr-3`}
                    style={{
                      width: 48, height: 48, borderRadius: '50%',
                      background: factors.length > 0 ? 'rgba(40,167,69,0.1)' : 'rgba(255,193,7,0.1)',
                      color: factors.length > 0 ? '#28a745' : '#ffc107',
                    }}>
                    <i className={`fas ${factors.length > 0 ? 'fa-shield-alt' : 'fa-exclamation-triangle'}`}></i>
                  </div>
                  <div>
                    <h5 className="font-weight-bold m-0">
                      {factors.length > 0 ? 'Two-Factor Enabled' : 'Two-Factor Disabled'}
                    </h5>
                    <small className="text-muted">
                      {factors.length > 0 
                        ? `Protected with ${factors.length} method${factors.length > 1 ? 's' : ''}` 
                        : 'Your account is not protected with 2FA'}
                    </small>
                  </div>
                </div>

                {factors.length === 0 && (
                  <div className="alert alert-warning" style={{ borderRadius: '12px' }}>
                    <i className="fas fa-info-circle mr-2"></i>
                    We strongly recommend enabling two-factor authentication to protect your account.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Enrolled Factors */}
        {factors.length > 0 && (
          <div className="row mb-4">
            <div className="col-md-8">
              <h6 className="font-weight-bold mb-3">Enrolled Methods</h6>
              {factors.map((factor, idx) => (
                <div key={idx} className="card mb-2" style={{ borderRadius: '12px' }}>
                  <div className="card-body p-3 d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <div className="d-flex align-items-center justify-content-center mr-3"
                        style={{ width: 40, height: 40, borderRadius: '10px', background: 'rgba(40,167,69,0.1)', color: '#28a745' }}>
                        <i className="fas fa-mobile-alt"></i>
                      </div>
                      <div>
                        <div className="font-weight-bold">{factor.displayName || 'Phone Number'}</div>
                        <div className="font-weight-bold">{factor.phoneNumber || 'Phone Number'}</div>
                        <small className="text-muted d-block">UID: {factor.uid?.substring(0, 8)}...</small>
                      </div>
                    </div>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleUnenroll(factor)}>
                      <i className="fas fa-trash mr-1"></i>Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add Button */}
        <div className="row">
          <div className="col-md-8">
            <button className="btn btn-primary" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }} onClick={openAddModal}>
              <i className="fas fa-plus mr-2"></i>{factors.length > 0 ? 'Add Another Method' : 'Enable Two-Factor Auth'}
            </button>
          </div>
        </div>

        {/* Add Factor Modal */}
        {showAddModal && (
          <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title font-weight-bold">
                    {step === 'phone' && 'Add Phone Number'}
                    {step === 'password' && 'Confirm Password'}
                    {step === 'verify' && 'Verify Code'}
                  </h5>
                  <button type="button" className="close" onClick={() => { setShowAddModal(false); setStep('phone'); }}>
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  {step === 'phone' && (
                    <>
                      <div className="form-group">
                        <label>Phone Number</label>
                        <input
                          type="tel"
                          className="form-control"
                          placeholder="(669) 000-0000"
                          value={phoneNumber}
                          onChange={e => setPhoneNumber(formatPhoneInput(e.target.value))}
                        />
                        <small className="text-muted">US number - auto-converts to +1</small>
                      </div>
                      <button className="btn btn-primary btn-block" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }}
                        onClick={handleStartEnroll} disabled={phoneNumber.replace(/\D/g, '').length !== 10}>
                        Continue
                      </button>
                    </>
                  )}

                  {step === 'password' && (
                    <>
                      <div className="form-group">
                        <label>Enter your password to continue</label>
                        <input
                          type="password"
                          className="form-control"
                          placeholder="Your password"
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                        />
                      </div>
                      <button className="btn btn-primary btn-block" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }}
                        onClick={handleSendCode} disabled={processing || !password}>
                        {processing ? <><i className="fas fa-spinner fa-spin mr-2"></i>Sending...</> : 'Send Verification Code'}
                      </button>
                      <div id="mfa-recaptcha"></div>
                    </>
                  )}

                  {step === 'verify' && (
                    <>
                      <p className="text-muted">Enter the 6-digit code sent to {phoneNumber}</p>
                      <div className="form-group">
                        <input
                          type="text"
                          className="form-control text-center"
                          style={{ fontSize: '24px', letterSpacing: '5px' }}
                          placeholder="000000"
                          value={code}
                          onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          maxLength={6}
                        />
                      </div>
                      <button className="btn btn-primary btn-block" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }}
                        onClick={handleVerifyAndEnroll} disabled={processing || code.length !== 6}>
                        {processing ? <><i className="fas fa-spinner fa-spin mr-2"></i>Verifying...</> : 'Verify & Enable'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Remove Factor Modal */}
        {showRemoveModal && (
          <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title font-weight-bold">
                    {removeStep === 'confirm' && 'Remove Two-Factor Authentication'}
                    {removeStep === 'password' && 'Enter Password'}
                    {removeStep === 'mfa-code' && 'Verify Your Identity'}
                  </h5>
                  <button type="button" className="close" onClick={() => { setShowRemoveModal(false); setSelectedFactorUid(null); }}>
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  {removeStep === 'confirm' && (
                    <>
                      <p className="text-muted">Are you sure you want to remove this authentication method?</p>
                      <div className="alert alert-warning">
                        <i className="fas fa-exclamation-triangle mr-2"></i>
                        Your account will be less secure without two-factor authentication.
                      </div>
                      <button className="btn btn-primary btn-block" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }}
                        onClick={() => setRemoveStep('password')}>
                        <i className="fas fa-sign-in-alt mr-2"></i>Continue with Password
                      </button>
                    </>
                  )}

                  {removeStep === 'password' && (
                    <>
                      <p className="text-muted">Enter your password to remove this security method.</p>
                      <div className="form-group">
                        <label>Password</label>
                        <input
                          type="password"
                          className="form-control"
                          placeholder="Your password"
                          value={removePassword}
                          onChange={e => setRemovePassword(e.target.value)}
                        />
                      </div>
                      <div className="d-flex">
                        <button className="btn btn-secondary mr-2" onClick={() => setRemoveStep('confirm')}>
                          Back
                        </button>
                        <button className="btn btn-danger flex-fill" onClick={handleRemoveReauth} disabled={processing || !removePassword}>
                          {processing ? <><i className="fas fa-spinner fa-spin mr-2"></i>Authenticating...</> : 'Authenticate & Remove'}
                        </button>
                      </div>
                    </>
                  )}

                  {removeStep === 'mfa-code' && (
                    <>
                      <p className="text-muted">Enter the 6-digit code sent to {removePhoneHint}</p>
                      <div className="form-group">
                        <input
                          type="text"
                          className="form-control text-center"
                          style={{ fontSize: '24px', letterSpacing: '5px' }}
                          placeholder="000000"
                          value={removeCode}
                          onChange={e => setRemoveCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          maxLength={6}
                          autoFocus
                        />
                      </div>
                      <button className="btn btn-danger btn-block"
                        onClick={handleRemoveMfaVerify} disabled={processing || removeCode.length !== 6}>
                        {processing ? <><i className="fas fa-spinner fa-spin mr-2"></i>Verifying...</> : <><i className="fas fa-check mr-2"></i>Verify & Remove</>}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default TwoFactorAuthPage;
